import type { Map as MapLibreMap, RasterTileSource } from 'maplibre-gl'

import type { Layer, RefreshOutcome } from '../layer'
import { getMapTemplate, TILE_SIZE, type WmsTarget } from './wms'

/**
 * A WMS layer with no time dimension — the NWS alerts polygons, which the
 * upstream renders as "whatever is in effect right now".
 *
 * That makes it the one layer whose tiles go stale in place, so while it is
 * visible a timer re-points the source at a cache-busting URL. Everything else
 * in the overlay stack carries an explicit TIME and never needs this.
 */

export interface WmsStaticOptions extends WmsTarget {
  id: string
  attribution: string
  opacity: number
  /** 0 disables the refresh timer. */
  refreshSeconds?: number
}

export class WmsStaticLayer implements Layer {
  readonly id: string
  readonly times = null
  private map: MapLibreMap | null = null
  private opacity: number
  private visible = true
  private timer: ReturnType<typeof setInterval> | null = null
  private stamp = 0
  /** When the tiles on screen were asked for, give or take the first add. */
  private refreshedAt = Date.now()

  constructor(private readonly opts: WmsStaticOptions) {
    this.id = opts.id
    this.opacity = opts.opacity
  }

  load(): Promise<void> {
    return Promise.resolve()
  }

  private get key(): string {
    return `wx:${this.id}`
  }

  private template(): string {
    const url = getMapTemplate(this.opts)
    return this.stamp ? `${url}&_=${this.stamp}` : url
  }

  add(map: MapLibreMap, beforeId?: string): void {
    this.map = map
    if (map.getLayer(this.key)) return
    map.addSource(this.key, {
      type: 'raster',
      tiles: [this.template()],
      tileSize: TILE_SIZE,
      attribution: this.opts.attribution,
    })
    map.addLayer(
      {
        id: this.key,
        type: 'raster',
        source: this.key,
        layout: { visibility: this.visible ? 'visible' : 'none' },
        paint: { 'raster-opacity': this.visible ? this.opacity : 0, 'raster-fade-duration': 0 },
      },
      beforeId,
    )
    this.schedule()
  }

  remove(map: MapLibreMap): void {
    this.stop()
    if (map.getLayer(this.key)) map.removeLayer(this.key)
    if (map.getSource(this.key)) map.removeSource(this.key)
    if (this.map === map) this.map = null
  }

  setOpacity(opacity: number): void {
    this.opacity = opacity
    if (this.map?.getLayer(this.key)) {
      this.map.setPaintProperty(this.key, 'raster-opacity', opacity)
    }
  }

  setTime(): void {
    // Time-invariant: the upstream always renders what is in effect now.
  }

  currentTime(): Date | null {
    return null
  }

  setVisible(visible: boolean): void {
    if (this.visible === visible) return
    this.visible = visible
    if (this.map?.getLayer(this.key)) {
      this.map.setLayoutProperty(this.key, 'visibility', visible ? 'visible' : 'none')
    }
    if (visible) this.schedule()
    else this.stop()
  }

  ready(): Promise<void> {
    const map = this.map
    if (!map) return Promise.resolve()
    return new Promise((resolve) => map.once('idle', () => resolve()))
  }

  private schedule(): void {
    const seconds = this.opts.refreshSeconds ?? 0
    if (!seconds || this.timer || !this.visible) return
    this.timer = setInterval(() => this.reload(), seconds * 1000)
  }

  private stop(): void {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
  }

  /**
   * Catch up after the page was hidden. The interval above freezes with a
   * sleeping phone and is throttled in a background tab, so an alert layer
   * could show warnings from before lunch for up to a minute after somebody
   * looked. Reloads only when the tiles are past their own cadence, which
   * makes this free to call on every return and every controller tick.
   */
  async refresh(canPatch: () => boolean): Promise<RefreshOutcome> {
    const seconds = this.opts.refreshSeconds ?? 0
    if (!seconds || !this.visible || !canPatch()) return 'unchanged'
    if (Date.now() - this.refreshedAt < seconds * 1000) return 'unchanged'
    this.reload()
    // Restart the cadence from now, or the old interval fires again seconds
    // after this and asks nowCOAST to render the same tiles twice.
    this.stop()
    this.schedule()
    return 'unchanged'
  }

  private reload(): void {
    this.refreshedAt = Date.now()
    this.stamp = Math.floor(this.refreshedAt / 1000)
    const source = this.map?.getSource(this.key) as RasterTileSource | undefined
    source?.setTiles?.([this.template()])
  }
}
